import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { MenuProvider } from './contexts/MenuContext';
import { ProtectedRoute, PublicRoute } from './components/auth/ProtectedRoute';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Dashboard } from './components/dashboard/Dashboard';
import { LandingPage } from './components/landing/LandingPage';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import { NaverCallback } from './components/auth/NaverCallback';
import { KakaoCallback } from './components/auth/KakaoCallback';
import { FindAccount, ResetPassword } from './components/auth/FindAccount';
import { PendingApproval, ApprovalRejected, Unauthorized } from './components/auth/StatusPages';
// Crawler Pages (ALT 텍스트 생성)
import AlttextUrl from './pages/crawler/alttextUrl';
import AlttextImgUrl from './pages/crawler/alttextImgUrl';
import AlttextImgUpload from './pages/crawler/alttextImgUpload';
import { UserManagement } from './components/admin/UserManagement';
import { MenuManagement } from './components/admin/MenuManagement';
import { BatchManagement } from './components/admin/batch/BatchManagement';
import { Profile, Withdraw } from './components/profile/ProfilePages';
// API Pages
import ApiDocsPage from './pages/api/apiDocs';

import ApiUsagePage from './pages/api/apiUsage';
// Admin Pages
import ApiKeyManagement from './pages/admin/ApiKeyManagement';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import SystemHealth from './pages/admin/SystemHealth';
// New AI Usage Pages
import AiUsageDailyPage from './pages/dashboard/AiUsageDaily';
import OpenApiUsagePage from './pages/api/OpenApiUsage';
import ContextProfilePage from './pages/profile/ContextProfilePage';
import AltContextItemsPage from './pages/admin/AltContextItemsPage';
// Notice & Inquiry Pages
import NoticeListPage from './pages/dashboard/notices/NoticeListPage';
import NoticeDetailPage from './pages/dashboard/notices/NoticeDetailPage';
import InquiryListPage from './pages/dashboard/inquiry/InquiryListPage';
import InquiryNewPage from './pages/dashboard/inquiry/InquiryNewPage';
import InquiryDetailPage from './pages/dashboard/inquiry/InquiryDetailPage';
import AdminNoticeList from './pages/admin/AdminNoticeList';
import AdminNoticeForm from './pages/admin/AdminNoticeForm';
import AdminInquiryList from './pages/admin/AdminInquiryList';
import AdminInquiryDetail from './pages/admin/AdminInquiryDetail';
// Plan & Billing Pages
import PlanPage from './pages/dashboard/plan/PlanPage';
import BillingPage from './pages/dashboard/plan/BillingPage';
import SubscriptionHistoryPage from './pages/dashboard/plan/SubscriptionHistoryPage';
import ReceiptInfoPage from './pages/dashboard/plan/ReceiptInfoPage';
import ChangePasswordPage from './pages/dashboard/profile/ChangePasswordPage';
import AdminPaymentList from './pages/admin/AdminPaymentList';
import AdminTeamList from './pages/admin/AdminTeamList';
// Team Pages
import TeamInviteAccept from './pages/team/TeamInviteAccept';
// Diagnose Pages (접근성 & SEO 무료 진단)
import DiagnosePage from './pages/diagnose/DiagnosePage';
import DiagnoseHistoryPage from './pages/diagnose/DiagnoseHistoryPage';
import PrivacyPage from './pages/PrivacyPage';
import { ExtensionGuide } from './pages/extension/ExtensionGuide';
import { ShopExtensionGuide } from './pages/extension/ShopExtensionGuide';
import SnippetGuide from './pages/snippet/SnippetGuide';
import UserGuide from './pages/guide/UserGuide';
import { ScrollToTop } from './components/layout/ScrollToTop';

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            {/* Landing Page */}
            <Route path="/" element={<LandingPage />} />

            {/* 접근성 & SEO 무료 진단 (비회원 공개) */}
            <Route path="/diagnose" element={<DiagnosePage />} />

            {/* 개인정보 처리방침 (비회원 공개) */}
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/auth/naver/callback" element={<NaverCallback />} />
            <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
            <Route path="/notices" element={<NoticeListPage />} />
            <Route path="/notices/:id" element={<NoticeDetailPage />} />
          <Route path="/find-account" element={<PublicRoute><FindAccount /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          
          {/* Status Pages */}
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/approval-rejected" element={<ApprovalRejected />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><MenuProvider><DashboardLayout /></MenuProvider></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            {/* Crawler Routes - 백엔드 menuPath와 일치 */}
            <Route path="crawler/website" element={<AlttextUrl />} />
            <Route path="crawler/image-url" element={<AlttextImgUrl />} />
            <Route path="crawler/upload" element={<AlttextImgUpload />} />
            {/* 진단 이력 (로그인 필요) */}
            <Route path="diagnose/history" element={<DiagnoseHistoryPage />} />
            {/* 외부 API 연동 신규 메뉴 (기존 기능과 완전 분리) */}
            <Route path="api-docs" element={<ApiDocsPage />} />
            <Route path="extension-guide" element={<ExtensionGuide />} />
            <Route path="shop-extension-guide" element={<ShopExtensionGuide />} />
            <Route path="snippet-guide" element={<SnippetGuide />} />
            <Route path="user-guide" element={<UserGuide />} />

            <Route path="api-usage" element={<ApiUsagePage />} />
            {/* AI 이용 현황 (일별 집계) - 대시보드 메뉴 하단 신규 */}
            <Route path="ai-usage-daily" element={<AiUsageDailyPage />} />
            {/* API 이용 현황 (OPEN_API 상세) - 이미지 API 메뉴 하단 신규 */}
            <Route path="open-api-usage" element={<OpenApiUsagePage />} />
            {/* Profile Routes */}
            <Route path="profile/edit" element={<Profile />} />
            <Route path="profile/withdraw" element={<Withdraw />} />
            <Route path="profile/context" element={<ContextProfilePage />} />
            <Route path="profile/change-password" element={<ChangePasswordPage />} />
            {/* Admin Routes */}
            <Route 
              path="admin/users" 
              element={
                <ProtectedRoute requiredRole="SUPER_ADMIN">
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="admin/menu" 
              element={
                <ProtectedRoute requiredRole="SUPER_ADMIN">
                  <MenuManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="admin/batch" 
              element={
                <ProtectedRoute requiredRole="SUPER_ADMIN">
                  <BatchManagement />
                </ProtectedRoute>
              } 
            />

            {/* Direct page routes for management */}
            {/* API Key Management - CLIENT도 접근 가능 (본인 키만 관리) */}
            <Route path="ApiKeyManagement" element={<ApiKeyManagement />} />
            <Route path="AdminAuditLogs" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminAuditLogs /></ProtectedRoute>} />
            <Route
              path="admin/system-health"
              element={
                <ProtectedRoute requiredRole="SUPER_ADMIN">
                  <SystemHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/alt-context-items"
              element={
                <ProtectedRoute requiredRole="SUPER_ADMIN">
                  <AltContextItemsPage />
                </ProtectedRoute>
              }
            />
            {/* Notice Routes */}
            <Route path="notices" element={<NoticeListPage />} />
            <Route path="notices/:id" element={<NoticeDetailPage />} />
            {/* Inquiry Routes */}
            <Route path="inquiry" element={<InquiryListPage />} />
            <Route path="inquiry/new" element={<InquiryNewPage />} />
            <Route path="inquiry/:id" element={<InquiryDetailPage />} />
            {/* Admin Notice Routes */}
            <Route path="admin/notices" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminNoticeList /></ProtectedRoute>} />
            <Route path="admin/notices/new" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminNoticeForm /></ProtectedRoute>} />
            <Route path="admin/notices/:id/edit" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminNoticeForm /></ProtectedRoute>} />
            {/* Admin Inquiry Routes */}
            <Route path="admin/inquiries" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminInquiryList /></ProtectedRoute>} />
            <Route path="admin/inquiries/:id" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminInquiryDetail /></ProtectedRoute>} />
            {/* Plan & Billing Routes */}
            <Route path="my/plan" element={<PlanPage />} />
            <Route path="my/billing" element={<BillingPage />} />
            <Route path="my/subscription-history" element={<SubscriptionHistoryPage />} />
            <Route path="my/receipt-info" element={<ReceiptInfoPage />} />
            {/* Admin Payment Routes */}
            <Route path="admin/payments" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminPaymentList /></ProtectedRoute>} />
            {/* Admin Team Routes */}
            <Route path="admin/teams" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminTeamList /></ProtectedRoute>} />
          </Route>

          {/* Team Invite Routes - 비로그인 사용자도 접근 가능 (내부에서 로그인 체크) */}
          <Route path="/invite/:token" element={<TeamInviteAccept />} />

          {/* Legacy Routes - Redirect to new structure */}
          <Route path="/crawler" element={<Navigate to="/dashboard/crawler/website" replace />} />
          <Route path="/crawling" element={<Navigate to="/dashboard/crawler/website" replace />} />
          <Route path="/url-input" element={<Navigate to="/dashboard/crawler/image-url" replace />} />
          <Route path="/upload" element={<Navigate to="/dashboard/crawler/upload" replace />} />

          {/* 404 Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </GoogleOAuthProvider>
    <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);